/** Plazo comercial de vencimiento de comprobantes (cliente → factura venta). */

import { toDateOnly, toLocalDateInputValue } from './facturaVentaFechas';

/** Valor especial en `vtoComprob` (byte en API): último día del mes de la fecha de emisión. */
export const VTO_COMPROB_FIN_DE_MES = 255;

export const VTO_COMPROB_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 7, label: '7 días' },
  { value: 14, label: '14 días' },
  { value: 30, label: '30 días' },
  { value: 45, label: '45 días' },
  { value: 60, label: '60 días' },
  { value: 90, label: '90 días' },
  { value: VTO_COMPROB_FIN_DE_MES, label: 'Fin de mes' },
];

export function resolveVtoComprobCliente(cliente: unknown): number | null {
  if (cliente == null || typeof cliente !== 'object') return null;
  const o = cliente as Record<string, unknown>;
  const raw = o.vtoComprob ?? o.VtoComprob ?? o.vto_comprob;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function isVtoComprobFinDeMes(vtoComprob: number | null | undefined): boolean {
  return vtoComprob === VTO_COMPROB_FIN_DE_MES;
}

export function labelVtoComprob(vtoComprob: number | null | undefined): string {
  if (vtoComprob == null) return 'Sin plazo (vence en la fecha de emisión)';
  const opt = VTO_COMPROB_OPTIONS.find((o) => o.value === vtoComprob);
  if (opt) return opt.label;
  if (vtoComprob > 0) return `${vtoComprob} días`;
  return 'Sin plazo';
}

export function lastDayOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Calcula fecha de vencimiento comercial según Vto. Comprob. del cliente. */
export function calcularFechaVencimDesdeVtoComprob(
  fechaEmision: string | Date,
  vtoComprob: number | null | undefined,
): Date {
  const emision = toDateOnly(fechaEmision) ?? new Date();
  if (vtoComprob == null) {
    return emision;
  }
  if (isVtoComprobFinDeMes(vtoComprob)) {
    return lastDayOfMonth(emision);
  }
  if (vtoComprob > 0 && vtoComprob < VTO_COMPROB_FIN_DE_MES) {
    const d = new Date(emision);
    d.setDate(d.getDate() + vtoComprob);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  return emision;
}

/** Infiere el plazo usado al emitir el comprobante a partir de emisión y vencimiento guardados. */
export function inferirVtoComprobDesdeFechas(
  fechaEmision: string | Date,
  fechaVencim: string | Date,
): number | null {
  const emision = toDateOnly(fechaEmision);
  const vencim = toDateOnly(fechaVencim);
  if (!emision || !vencim) return null;
  if (vencim.getTime() === emision.getTime()) return null;
  if (vencim.getTime() === lastDayOfMonth(emision).getTime()) {
    return VTO_COMPROB_FIN_DE_MES;
  }
  const diffDays = Math.round((vencim.getTime() - emision.getTime()) / 86_400_000);
  if (diffDays > 0 && diffDays < VTO_COMPROB_FIN_DE_MES) {
    return diffDays;
  }
  return null;
}

/** Fecha mínima permitida (sugerida) en input type="date" (YYYY-MM-DD). */
export function fechaVencimMinimaYmd(
  fechaEmision: string | Date,
  vtoComprob: number | null | undefined,
): string {
  return toLocalDateInputValue(calcularFechaVencimDesdeVtoComprob(fechaEmision, vtoComprob));
}
